import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Play, Pause, CheckCircle2, AlertOctagon, Camera,
  Star, Clock, Bed, ChevronLeft, RefreshCw, PartyPopper, Coffee, X,
  Bath, Wind, Fan, ShoppingBag, Wine, Monitor, Trees, Volume2,
} from "lucide-react";

const REFRESH_MS = 20_000;

const ICON = {
  bathroom: Bath, bed: Bed, dusting: Wind, vacuum: Fan,
  amenities: ShoppingBag, mini_bar: Wine, windows: Monitor, balcony: Trees,
};

const ISSUE_CATEGORIES = [
  { key: "maintenance",       label: "Maintenance",       color: "#f59e0b" },
  { key: "lost_and_found",    label: "Lost & Found",      color: "#38bdf8" },
  { key: "guest_belongings",  label: "Guest belongings",  color: "#a78bfa" },
  { key: "broken_furniture",  label: "Broken furniture",  color: "#ef4444" },
  { key: "deep_cleaning",     label: "Deep cleaning",     color: "#22d3ee" },
  { key: "missing_inventory", label: "Missing inventory", color: "#ec4899" },
  { key: "water_leakage",     label: "Water leakage",     color: "#06b6d4" },
  { key: "electrical",        label: "Electrical",        color: "#f43f5e" },
];

export default function HKCopilot() {
  const [screen, setScreen] = useState("home"); // home | room | cleaning | report | done
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState({}); // { key: true/false }
  const [tick, setTick] = useState(0);
  const timerRef = useRef();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/housekeeping/copilot/summary");
      setData(r.data);
      // Hydrate checklist from active task
      const active = r.data?.active_task;
      const map = active?.meta?.checklist || {};
      const state = {};
      Object.entries(map).forEach(([k, v]) => { state[k] = !!v?.checked; });
      setChecklist(state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load copilot");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    timerRef.current = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const attendant  = data?.attendant;
  const shift      = data?.shift;
  const activeTask = data?.active_task;
  const nextTask   = data?.next_task;
  const checklistTpl = data?.checklist_template || [];
  const announcements = data?.announcements || [];

  const timerLive = useMemo(() => {
    if (!activeTask?.started_at) return null;
    try {
      const s = new Date(activeTask.started_at);
      const secs = Math.floor((Date.now() - s.getTime()) / 1000);
      const mm = Math.floor(secs / 60);
      const ss = secs % 60;
      return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask?.started_at, tick]);

  const progressPct = checklistTpl.length
    ? Math.round((Object.values(checklist).filter(Boolean).length / checklistTpl.length) * 100)
    : 0;

  // ── Actions ───────────────────────────────────────────
  const startCleaning = async (task) => {
    setBusy(true);
    try {
      await api.post(`/housekeeping/tasks/${task.id}/accept`).catch(() => {});
      await api.post(`/housekeeping/tasks/${task.id}/start`);
      toast.success("Cleaning started");
      setChecklist({});
      await load(true);
      setScreen("cleaning");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally { setBusy(false); }
  };

  const toggleChecklist = async (key) => {
    const next = !checklist[key];
    setChecklist((c) => ({ ...c, [key]: next }));
    if (activeTask?.id) {
      try {
        await api.post("/housekeeping/copilot/checklist/update", {
          task_id: activeTask.id, key, checked: next,
        });
      } catch { /* optimistic */ }
    }
  };

  const pauseCleaning = async () => {
    if (!activeTask?.id) return;
    try {
      await api.post(`/housekeeping/tasks/${activeTask.id}/pause`);
      toast.success("Paused");
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resumeCleaning = async () => {
    if (!activeTask?.id) return;
    try {
      await api.post(`/housekeeping/tasks/${activeTask.id}/resume`);
      toast.success("Resumed");
      await load(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const completeRoom = async () => {
    if (!activeTask?.id) return;
    if (progressPct < 100 && !window.confirm("Checklist isn't fully complete. Mark room done anyway?")) return;
    setBusy(true);
    try {
      await api.post(`/housekeeping/tasks/${activeTask.id}/complete`);
      toast.success("Room complete");
      setScreen("done");
      setChecklist({});
      setTimeout(() => { setScreen("home"); load(true); }, 1800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete");
    } finally { setBusy(false); }
  };

  // ── Render ──────────────────────────────────────────
  return (
    <div className="on-dark min-h-full text-white relative overflow-hidden" data-testid="hk-copilot"
      style={{ background: "#0B1020" }}>
      {/* Ambient mesh */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full opacity-[0.16] blur-3xl"
             style={{ background: "radial-gradient(circle, #f59e0b 0%, transparent 70%)" }}/>
        <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full opacity-[0.14] blur-3xl"
             style={{ background: "radial-gradient(circle, #38bdf8 0%, transparent 70%)" }}/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,16,32,0.55) 0%, rgba(11,16,32,0.30) 30%, rgba(11,16,32,0.30) 70%, rgba(11,16,32,0.55) 100%)" }}/>
      </div>

      <div className="relative z-10 mx-auto max-w-md min-h-full flex flex-col p-5 pb-24">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-white/70">Loading your shift…</div>
        ) : screen === "home" ? (
          <HomeScreen
            attendant={attendant} shift={shift} activeTask={activeTask} nextTask={nextTask}
            announcements={announcements}
            timerLive={timerLive}
            onResume={() => setScreen("cleaning")}
            onNext={() => nextTask ? setScreen("room") : toast("No more rooms in queue.")}
            onReload={() => load(true)}
          />
        ) : screen === "room" ? (
          <RoomScreen
            task={nextTask}
            busy={busy}
            onBack={() => setScreen("home")}
            onStart={() => nextTask && startCleaning(nextTask)}
            onReport={() => setScreen("report")}
          />
        ) : screen === "cleaning" ? (
          <CleaningScreen
            task={activeTask} checklistTpl={checklistTpl}
            checklist={checklist} onToggle={toggleChecklist}
            timerLive={timerLive} progressPct={progressPct}
            onPause={pauseCleaning} onResume={resumeCleaning}
            onComplete={completeRoom} busy={busy}
            onReport={() => setScreen("report")}
            onBack={() => setScreen("home")}
          />
        ) : screen === "report" ? (
          <ReportIssueScreen
            task={activeTask || nextTask}
            onBack={() => setScreen(activeTask ? "cleaning" : nextTask ? "room" : "home")}
            onSubmitted={async () => { await load(true); setScreen(activeTask ? "cleaning" : "home"); }}
          />
        ) : screen === "done" ? (
          <DoneScreen room={activeTask}/>
        ) : null}
      </div>
    </div>
  );
}


/* ══════════ Screens ══════════ */

function HomeScreen({ attendant, shift, activeTask, nextTask, announcements, timerLive, onResume, onNext, onReload }) {
  return (
    <>
      <header className="mb-5" data-testid="hk-copilot-home">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-300">Housekeeping Copilot</div>
            <h1 className="font-display text-[26px] font-bold text-white leading-tight mt-1">
              Good day,{" "}
              <span className="bg-gradient-to-r from-amber-300 to-fuchsia-300 bg-clip-text text-transparent">{attendant?.name || "Attendant"}</span>
            </h1>
          </div>
          <button onClick={onReload} data-testid="hk-copilot-refresh"
            className="w-11 h-11 rounded-full bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.14] inline-flex items-center justify-center">
            <RefreshCw className="w-4 h-4"/>
          </button>
        </div>
      </header>

      {/* Progress ring + KPIs */}
      <ProgressRing shift={shift}/>

      {/* Active task takes over if running */}
      {activeTask && (
        <button onClick={onResume} data-testid="hk-copilot-resume"
          className="mt-5 rounded-3xl border border-amber-500/40 bg-gradient-to-br from-amber-500/30 to-orange-500/20 p-5 text-left w-full active:scale-[0.98] transition-transform">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-widest text-amber-200 font-semibold">Currently cleaning</div>
            <div className="flex items-center gap-1.5 text-white text-[13px] tabular-nums">
              <Clock className="w-3.5 h-3.5 text-amber-200"/> {timerLive || "00:00"}
            </div>
          </div>
          <div className="font-display text-[38px] font-bold text-white leading-none tabular-nums">Room {activeTask._room_number}</div>
          <div className="text-[13px] text-white/85 mt-1">Tap to resume checklist →</div>
        </button>
      )}

      {/* NEXT ROOM — the primary button */}
      {!activeTask && (
        <button
          onClick={onNext}
          data-testid="hk-copilot-next-room"
          disabled={!nextTask}
          className={`mt-5 w-full h-[92px] rounded-3xl font-display font-bold text-[22px] transition-all active:scale-[0.97] ${
            nextTask
              ? "bg-gradient-to-br from-amber-400 to-fuchsia-500 text-white shadow-[0_20px_60px_rgba(217,70,239,0.35)] hover:shadow-[0_20px_60px_rgba(245,158,11,0.45)]"
              : "bg-white/[0.08] text-white/60 border border-white/[0.14]"
          }`}
        >
          <div className="flex items-center justify-center gap-3">
            {nextTask ? <><span>NEXT ROOM</span><ArrowRight className="w-6 h-6"/></> : <span>All caught up</span>}
          </div>
          {nextTask && (
            <div className="text-[13px] font-normal text-white/90 mt-1 tabular-nums">
              Room {nextTask._room_number} · {nextTask._room_type_id || ""}{nextTask._is_vip ? " · VIP" : ""}
            </div>
          )}
        </button>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="mt-6" data-testid="hk-copilot-announcements">
          <div className="text-[10.5px] uppercase tracking-widest font-semibold text-white/80 mb-2">Supervisor</div>
          {announcements.map((m) => (
            <div key={m.id} className="rounded-2xl bg-white/[0.05] border border-white/[0.14] px-4 py-3 mb-2">
              <div className="text-[11px] text-amber-200 font-semibold">{m.from}</div>
              <div className="text-[13px] text-white mt-0.5">{m.text}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function ProgressRing({ shift }) {
  const total = Math.max(1, shift?.assigned_total || 0);
  const done = shift?.completed_today || 0;
  const pct = Math.round((done / total) * 100);
  const size = 160;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="flex items-center gap-5 rounded-3xl bg-white/[0.05] border border-white/[0.14] p-4">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={stroke}/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="url(#g1)" strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.22, 1, 0.36, 1)" }}/>
          <defs>
            <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#f59e0b"/><stop offset="1" stopColor="#d946ef"/>
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display font-bold text-white text-[34px] leading-none tabular-nums">{done}</div>
          <div className="text-[10.5px] uppercase tracking-widest text-white/70">of {total}</div>
        </div>
      </div>
      <div className="flex-1 space-y-2">
        <Stat label="Remaining" value={shift?.remaining || 0}/>
        <Stat label="Avg time" value={`${shift?.avg_cleaning_min || 0}m`} tone="ok"/>
        <Stat label="Est. finish" value={shift?.est_finish_min ? `${Math.round(shift.est_finish_min / 60)}h ${shift.est_finish_min % 60}m` : "—"}/>
        <Stat label="Score" value={`${shift?.current_score || 0}%`} tone="ok"/>
      </div>
    </div>
  );
}
function Stat({ label, value, tone }) {
  const c = tone === "ok" ? "text-emerald-300" : "text-white";
  return (
    <div className="flex items-baseline justify-between text-[13px]">
      <span className="text-white/70">{label}</span>
      <span className={`font-semibold tabular-nums ${c}`}>{value}</span>
    </div>
  );
}


function RoomScreen({ task, busy, onBack, onStart, onReport }) {
  if (!task) return null;
  return (
    <div data-testid="hk-copilot-room-screen">
      <button onClick={onBack} className="text-white/70 mb-4 inline-flex items-center gap-1 text-[13px]">
        <ChevronLeft className="w-4 h-4"/> Back
      </button>
      <div className="rounded-3xl bg-white/[0.05] border border-white/[0.14] p-6 mb-5">
        <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-2">Next Room</div>
        <div className="font-display font-bold text-white text-[64px] leading-none tabular-nums">
          {task._room_number}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {task._is_vip && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-fuchsia-500/25 border border-fuchsia-500/60 text-fuchsia-100 text-[11px] font-semibold uppercase tracking-widest">
              <Star className="w-3 h-3"/> VIP
            </span>
          )}
          {task.priority && (
            <span className="px-2 py-0.5 rounded-md bg-amber-500/25 border border-amber-500/60 text-amber-100 text-[11px] font-semibold uppercase tracking-widest">
              {task.priority}
            </span>
          )}
          {task._room_type_id && (
            <span className="px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/[0.14] text-white text-[11px] uppercase tracking-widest">
              {task._room_type_id}
            </span>
          )}
        </div>
        <div className="mt-4 space-y-2 text-[13px] text-white/85">
          {task._guest_name && <div><span className="text-white/70">Guest · </span>{task._guest_name}</div>}
          {task._next_arrival && <div><span className="text-white/70">Next arrival · </span>{task._next_arrival}</div>}
          {task.notes && <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-amber-100">Note · {task.notes}</div>}
        </div>
      </div>

      <button
        onClick={onStart} disabled={busy}
        data-testid="hk-copilot-start"
        className="w-full h-[74px] rounded-3xl font-display font-bold text-[20px] bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-[0_20px_60px_rgba(16,185,129,0.35)] active:scale-[0.97] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-3">
        <Play className="w-6 h-6" strokeWidth={2.5}/> Start Cleaning
      </button>

      <button onClick={onReport} data-testid="hk-copilot-report-from-room"
        className="mt-3 w-full h-12 rounded-2xl bg-white/[0.06] border border-white/[0.14] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2">
        <AlertOctagon className="w-4 h-4 text-amber-300"/> Report an issue
      </button>
    </div>
  );
}


function CleaningScreen({ task, checklistTpl, checklist, onToggle, timerLive, progressPct, onPause, onResume, onComplete, busy, onReport, onBack }) {
  if (!task) return null;
  const paused = task.state === "paused";
  return (
    <div data-testid="hk-copilot-cleaning-screen">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="text-white/70 inline-flex items-center gap-1 text-[13px]">
          <ChevronLeft className="w-4 h-4"/> Home
        </button>
        <div className="text-[12px] text-white flex items-center gap-1.5 tabular-nums" data-testid="hk-copilot-timer">
          <Clock className="w-3.5 h-3.5 text-amber-300"/>{timerLive || "00:00"}
        </div>
      </div>

      {/* Room hero */}
      <div className="rounded-3xl bg-white/[0.05] border border-white/[0.14] px-5 py-4 mb-4">
        <div className="flex items-baseline justify-between">
          <div className="font-display font-bold text-white text-[38px] leading-none tabular-nums">Room {task._room_number}</div>
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-widest text-white/70 font-semibold">Progress</div>
            <div className="font-display font-bold text-white text-[22px] leading-none tabular-nums">{progressPct}%</div>
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg,#f59e0b,#d946ef)" }}/>
        </div>
      </div>

      {/* Checklist */}
      <div className="space-y-2 mb-5" data-testid="hk-copilot-checklist">
        {checklistTpl.map((item) => {
          const Ic = ICON[item.key] || CheckCircle2;
          const checked = !!checklist[item.key];
          return (
            <button key={item.key}
              data-testid={`hk-copilot-check-${item.key}`}
              onClick={() => onToggle(item.key)}
              className={`w-full text-left rounded-2xl border transition-all px-4 py-4 flex items-center gap-4 active:scale-[0.98] ${
                checked
                  ? "bg-emerald-500/20 border-emerald-500/60"
                  : "bg-white/[0.05] border-white/[0.14] hover:bg-white/[0.10]"}`}>
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${
                checked ? "bg-emerald-500 text-white" : "bg-white/10 text-white/80"}`}>
                {checked ? <CheckCircle2 className="w-6 h-6" strokeWidth={2.5}/> : <Ic className="w-5 h-5"/>}
              </div>
              <div className="flex-1">
                <div className={`text-[16px] font-semibold ${checked ? "text-emerald-100 line-through decoration-emerald-400/60" : "text-white"}`}>
                  {item.label}
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 transition-all ${checked ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
                {checked && <CheckCircle2 className="w-full h-full text-white"/>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Bottom actions */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button onClick={onReport} data-testid="hk-copilot-report"
          className="h-12 rounded-2xl bg-white/[0.06] border border-white/[0.14] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2">
          <AlertOctagon className="w-4 h-4 text-amber-300"/> Report issue
        </button>
        {paused ? (
          <button onClick={onResume} data-testid="hk-copilot-resume-btn"
            className="h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/60 text-emerald-100 text-[13.5px] font-semibold inline-flex items-center justify-center gap-2">
            <Play className="w-4 h-4"/> Resume
          </button>
        ) : (
          <button onClick={onPause} data-testid="hk-copilot-pause"
            className="h-12 rounded-2xl bg-white/[0.06] border border-white/[0.14] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2">
            <Pause className="w-4 h-4"/> Pause
          </button>
        )}
      </div>

      <button
        onClick={onComplete} disabled={busy}
        data-testid="hk-copilot-complete"
        className="w-full h-[74px] rounded-3xl font-display font-bold text-[20px] bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-[0_20px_60px_rgba(16,185,129,0.35)] active:scale-[0.97] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-3">
        <CheckCircle2 className="w-6 h-6" strokeWidth={2.5}/> Complete room
      </button>
    </div>
  );
}


function ReportIssueScreen({ task, onBack, onSubmitted }) {
  const [cat, setCat] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!cat) { toast.error("Pick a category"); return; }
    setBusy(true);
    try {
      await api.post("/housekeeping/copilot/report-issue", {
        task_id: task?.id, room_id: task?.room_id,
        category: cat.key, title: cat.label, description: note || null,
      });
      toast.success("Issue reported");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to report");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="hk-copilot-report-screen">
      <button onClick={onBack} className="text-white/70 mb-4 inline-flex items-center gap-1 text-[13px]">
        <ChevronLeft className="w-4 h-4"/> Back
      </button>
      <h2 className="font-display text-[22px] font-bold text-white mb-1">Report an issue</h2>
      <p className="text-[13px] text-white/70 mb-4">Tap a category. We&apos;ll create a maintenance ticket and notify Mission Control automatically.</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {ISSUE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c)}
            data-testid={`hk-copilot-issue-${c.key}`}
            className={`h-24 rounded-2xl border text-left px-4 py-3 transition-all ${
              cat?.key === c.key
                ? "border-white/60 bg-white/[0.14]"
                : "border-white/[0.14] bg-white/[0.05] hover:bg-white/[0.10]"}`}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: `${c.color}30`, border: `1px solid ${c.color}80` }}>
              <AlertOctagon className="w-4 h-4" style={{ color: c.color }}/>
            </div>
            <div className="text-[13.5px] font-semibold text-white leading-tight">{c.label}</div>
          </button>
        ))}
      </div>

      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note (or use voice)…"
        data-testid="hk-copilot-issue-note"
        className="w-full rounded-2xl bg-white/[0.05] border border-white/[0.14] text-white p-4 text-[14px] outline-none focus:border-white/40 placeholder:text-white/40 mb-3"
      />

      <button
        onClick={submit} disabled={!cat || busy}
        data-testid="hk-copilot-issue-submit"
        className="w-full h-[62px] rounded-3xl font-display font-bold text-[18px] bg-gradient-to-br from-amber-400 to-rose-500 text-white shadow-[0_20px_50px_rgba(244,63,94,0.30)] active:scale-[0.97] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2">
        <Camera className="w-5 h-5"/> Submit report
      </button>
    </div>
  );
}


function DoneScreen({ room }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center" data-testid="hk-copilot-done-screen">
      <div className="w-24 h-24 rounded-3xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center mb-5 animate-bounce">
        <PartyPopper className="w-12 h-12 text-emerald-300"/>
      </div>
      <div className="font-display text-[32px] font-bold text-white mb-1">Nice work!</div>
      <div className="text-[15px] text-white/80">Room {room?._room_number} is ready.</div>
      <div className="text-[12.5px] text-white/60 mt-2">Mission Control has been updated.</div>
    </div>
  );
}
